import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';

import { 
  SET_UNREAD_MESSAGES_DIALOGUE_OPEN, SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, ADD_PROJECT_MEMBER, SiteboardApiValidator,
  CREATE_STANDARD_SUBSCRIPTION_CHECKOUT, DateStringMMDDYYYY, GET_PROJECT_SCHEDULE_CONTACTS, SET_PROJECT_CONTACTS_DIALOGUE_ID, SET_EDIT_PROJECT_DIALOGUE_ID,
} from '../actions/types';
import { setPath, setProfileDialogueShowing, siteboardAction } from '../actions/defaultActions';
import { getProjectDaysRemaining, getProjectCompletionDatestring, millisDurationToString, keyCountInObject } from '../Util';

import AlertCountBubble from '../components/AlertCountBubble';
import DarkModeToggle from './DarkModeToggle';
import PushButton from './PushButton';
import UserAvatar from './UserAvatar';

// icon imports
import DashboardIcon from '../icons/dashboardIcon';
import GlobeIcon from '../icons/globeIcon';
import GantChartIcon from '../icons/gantChartIcon';
import ExpandDownIcon from '../icons/expandDownIcon';
import Logo from '../SiteBoard_Logo';
import DotsIcon from '../icons/dotsIcon';
import MenuIcon from '../icons/menuIcon';
import CloseIcon from '../icons/closeIcon';
import NotepadIcon from '../icons/notepadIcon';
import NotificationIcon from '../icons/notificationIcon';
import UpgradeIcon from '../icons/upgradeIcon';
import SettingsIcon from '../icons/settingsIcon';
import TeamIcon from '../icons/teamIcon';
import ContactIcon from '../icons/contactIcon';

const oneDayInMillis = 1000*60*60*24;

const Topbar = ({ _projects, _schedules, _path, _users, _userId, _lastViewedProjectId, _subscriptions, _unreadScheduleMessageThreads, _subscriptionInvites, setPath, setProfileDialogueShowing, siteboardAction }) => {

  const backdropRef = useRef(null);
  const mouseDownOnBackdrop = useRef(false);
  const isTouchDevice = useRef(false);

  const iconSize = '24';
  const iconWidthHeightStyle = 'w-[24px] h-[24px]';
  const projectIdMatch = _path.match(/projectId#([a-fA-F0-9-]+)/);
  //const projectId = projectIdMatch !== null ? projectIdMatch[1] : _lastViewedProjectId;
  const projectId = projectIdMatch !== null ? projectIdMatch[1] : null;
  const selectedProject = projectId in _projects ? _projects[projectId] : null;
  const selectedSubscription = selectedProject !== null ? _subscriptions[selectedProject.subscriptionId] : null;

  const [projectDaysRemaining, setProjectDaysRemaining] = useState(selectedProject !== null ? getProjectDaysRemaining(selectedProject.beginDate, selectedProject.calendarDays) : '?');
  const [projectCompletionDate, setProjectCompletionDate] = useState(selectedProject !== null ? getProjectCompletionDatestring(selectedProject.beginDate, selectedProject.calendarDays) : '?');
  const [isExpanded, setIsExpanded] = useState(false);

  const updateProjectDaysRemaingTimer = useRef(null);

  const isMyProjectsPath = _path === '' || !(projectId in _projects);
  const isProjectDashbardPath = _path.match(/^projectId#[a-fA-F0-9-]+$/) !== null;
  const is3WeekSchedulePath = _path.match(/^projectId#([a-fA-F0-9-]+)#scheduleId#([a-fA-F0-9-]+)$/) || _path.match(/^projectId#([a-fA-F0-9-]+)#schedule$/) !== null;
  const isDailyReportPath = _path.match(/^projectId#([a-fA-F0-9-]+)#dailyReportId#([a-fA-F0-9-]+)$/) || _path.match(/^projectId#([a-fA-F0-9-]+)#dailyReports$/) !== null;

  const getUserEmail = () => { try { return _users[_userId].email; } catch(error) { return ''; } };
  const getUserName = () => { try { return _users[_userId].firstName + ' ' + _users[_userId].lastName; } catch(error) { return ''; } };

  let totalUnreadMessages = 0;
  for(const threadId in _unreadScheduleMessageThreads) {
    if(!(_unreadScheduleMessageThreads[threadId].projectId in _projects)) continue;
    totalUnreadMessages += _unreadScheduleMessageThreads[threadId].unreadCount;
  }
  totalUnreadMessages += keyCountInObject(_subscriptionInvites);

  let isTrialSubscription = false;
  let trialTimeRemainingString = null;
  let ownedSubscription = null;
  for(const subscriptionId in _subscriptions) {
    const subscription = _subscriptions[subscriptionId];
    if(!(_userId in subscription.members)) continue;
    if(subscription.members[_userId].role !== 'Owner') continue;
    if(subscription.type !== 'Trial') continue;

    // the current user owns this trial subscription
    isTrialSubscription = true;
    ownedSubscription = subscription;
    const trialTimeRemainingMillis = subscription.endTimestamp - new Date().getTime();
    //const trialTimeRemainingMillis = oneDayInMillis*7;
    //if(trialTimeRemainingMillis < oneDayInMillis*10) { // if time until trial expiration is less than 10 days display a notice
      if(trialTimeRemainingMillis < 0) {
        trialTimeRemainingString = 'Trial expired ' + millisDurationToString(Math.abs(trialTimeRemainingMillis)) + ' ago';
      } else {
        trialTimeRemainingString = 'Trial expires in ' + millisDurationToString(trialTimeRemainingMillis);
      }
    //}
  }
  const memberCountInActiveProject = selectedProject === null ? 0 : (keyCountInObject(selectedProject.members) + keyCountInObject(selectedProject.pendingMembers));

  // Update projectDaysRemaining every second
  useEffect(() => {
    const doUpdate = () => {
      try { 
        setProjectDaysRemaining(getProjectDaysRemaining(selectedProject.beginDate, selectedProject.calendarDays)); 
      } catch(error) {
        setProjectDaysRemaining('?');
      }
      try { 
        setProjectCompletionDate(getProjectCompletionDatestring(selectedProject.beginDate, selectedProject.calendarDays)); 
      } catch(error) {
        setProjectDaysRemaining('?');
      }
    };
    clearInterval(updateProjectDaysRemaingTimer.current);
    updateProjectDaysRemaingTimer.current = setInterval(() => { doUpdate(); }, 1000);
    doUpdate();

    return () => { clearInterval(updateProjectDaysRemaingTimer.current); }; // remove interval timeout when component unloads
  }, [selectedProject, projectId, _projects]);

  return (
  <>
    {isExpanded && 
      <div 
        ref={backdropRef}  
        className='fixed z-[5] top-0 left-0 h-screen w-screen flex justify-center items-center bg-[#000000aa] cursor-pointer' 
        onTouchStart = {(event) => {
          // detect touches devices with this event
          isTouchDevice.current = true;
        }}
        onMouseDown = {(event) => {
          // using mousedown/mouseup to trigger closing the window by clicking backdrop ref because it works better than onclick
          // we can ensure both mouseup and mousedown events occured on the backdrop ref before closing the popup
          if(event.target !== backdropRef.current) return;
          mouseDownOnBackdrop.current = true;
        }}
        onMouseUp = {(event) => {
          if(event.target !== backdropRef.current) return;
          if(!mouseDownOnBackdrop.current) return;
          mouseDownOnBackdrop.current = false;
          try { setIsExpanded(false); } catch(error) {}
        }}
        onClick={(event) => {
          if(event.target !== backdropRef.current) return;
          if(!isTouchDevice.current) return; // only using this method of closing the popup for touch devices
          try { setIsExpanded(false); } catch(error) {}
        }}
      >
      </div>
    }

    <div className='relative bg-black/90 text-white z-[6]'>

      {/* Collapsed view top nav */}
      <div className={'xl:hidden flex flex justify-between items-center gap-x-4 px-[5px] py-[6px]'}>
        {/* SiteBoard logo */}
        <button  
          className={'flex font-bold items-center gap-x-2 text-white text-[1.6em]'}
          onClick={() => { 
            setPath(''); 
            setIsExpanded(false);
          }}
        >
          <Logo size={iconSize} />
          {process.env.REACT_APP_WEBSITE_NAME} 
        </button>
        <div className='grow flex gap-x-4 justify-end px-2'>
          {/* My projects button */}
          <button  
            className={`flex items-center gap-x-[10px] group transition-opactity duration-300 ${isExpanded || selectedProject === null ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            onClick={() => { setPath(''); }}
          >
            {/*<div className={isMyProjectsPath ? 'hidden' : 'group-hover:hidden'}>
              <GlobeIcon size={iconSize} />
            </div>
            <div className={isMyProjectsPath ? '' : 'hidden group-hover:block'}>
              <GlobeIcon size={iconSize} color='#fff168' />
            </div>*/}
            <div>
              <GlobeIcon size={iconSize} color='#fff168' />
            </div>
          </button>

          {/* Project dashboard button */}
          {/*<button  
            className={`flex items-center gap-x-[10px] group ${isExpanded || selectedProject === null ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            onClick={() => { setPath('projectId#' + selectedProject.id); }}
          >
            <div className={isProjectDashbardPath ? 'hidden' : 'group-hover:hidden'}>
              <DashboardIcon size={iconSize} />
            </div>
            <div className={isProjectDashbardPath ? '' : 'hidden group-hover:block'}>
              <DashboardIcon size={iconSize} color='#1267e7' />
            </div>
          </button>*/}

          {/* 3-week schedule button */}
          {/*<button  
            className={`flex items-center gap-x-[10px] group ${isExpanded || selectedProject === null ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            onClick={() => { setPath('projectId#' + selectedProject.id + '#schedule'); }}
          >
            <div className={is3WeekSchedulePath ? 'hidden' : 'group-hover:hidden'}>
              <GantChartIcon size={iconSize} />
            </div>
            <div className={is3WeekSchedulePath ? '' : 'hidden group-hover:block'}>
              <GantChartIcon size={iconSize} color='#471cff' />
            </div>
          </button>*/}

          {/* Daily Reports button */}
          {process.env.REACT_APP_DAILY_REPORTS_ENABLE === 'true' &&
            <button  
              className={`flex items-center gap-x-[10px] group ${isExpanded || selectedProject === null ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
              onClick={() => { setPath('projectId#' + selectedProject.id + '#dailyReports'); }}
            >
              <div className={isDailyReportPath ? 'hidden' : 'group-hover:hidden'}>
                <NotepadIcon size='24' color='#a59e9e' />
              </div>
              <div className={isDailyReportPath ? '' : 'hidden group-hover:block'}>
                <NotepadIcon size='24' color='#55fa79' />
              </div>
            </button>
          }

          {/* Unread Message button */}
          <button 
            className={`group relative flex justify-start items-center gap-x-2 font-semibold text-[#a59e9e] mr-2 ${isExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            onClick={() => {
              siteboardAction(SET_UNREAD_MESSAGES_DIALOGUE_OPEN, { isOpen: true });
            }}
          >
            <div className='group-hover:hidden'><NotificationIcon /></div> 
            <div className='hidden group-hover:block'><NotificationIcon color='#fff' /></div> 
            <div className='absolute top-0 left-full translate-x-[-35%] translate-y-[-25%]'><AlertCountBubble totalUnreadMessages={totalUnreadMessages} /></div>
          </button>

          {/* Dark Mode Toggle */}
          {selectedProject !== null &&
            <div className='relative top-[2px]'>
              <DarkModeToggle small={true} />
            </div>
          }

          {/* Expand Topbar button */}
          <button
            className={`relative flex items-center justify-center gap-x-2 rounded-[5px]`}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
            title='Expand Topbar'
          >
            <div className={`${iconWidthHeightStyle} ${isExpanded ? 'opacity-0' : 'opacity-100'}`}><MenuIcon size={iconSize} /></div>
            <div className={`absolute ${iconWidthHeightStyle} ${isExpanded ? 'opacity-100' : 'opacity-0'}`}><CloseIcon hoverColor='#fff' /></div>
          </button>
        </div>
      </div>

      {/* Drop-Down part of the menu */}
      {isExpanded && 
        <>
          <div className='absolute z-[5] flex flex-col top-full w-screen bg-black/90 text-white  max-h-[90vh] overflow-y-auto animate-up-fade'>
            {selectedProject !== null &&
              <div className='flex flex-col mt-2 mx-[14px] gap-2'>
                {/* Project info */}
                <div className='flex flex-col gap-1'>
                  <div className={`flex items-end gap-x-1 text-[#a59e9e]`}>
                    <div className='opacity-80'>Project:</div><div className='text-white font-semibold'>{selectedProject.name}</div>
                  </div>
                  {/* Only show job number if it is set */}
                  {(typeof selectedProject.jobNumber === 'string' && selectedProject.jobNumber.length > 1) &&
                    <div className={`flex items-end gap-x-1 text-[#a59e9e]`}>
                      <div className='opacity-80'>Job#:</div><div className='text-white font-semibold'>{selectedProject.jobNumber}</div>
                    </div>
                  }
                  {DateStringMMDDYYYY.isValid(selectedProject.beginDate) &&
                    <div className='flex items-end gap-x-1 text-[#a59e9e]'>
                      <div className='opacity-80'>Notice to proceed:</div><div className='text-white font-semibold'>{selectedProject.beginDate}</div>
                    </div>
                  }
                  {/* Only show project days remaining if we are able to calculate it from the project settings */}
                  {(DateStringMMDDYYYY.isValid(selectedProject.beginDate) && typeof selectedProject.calendarDays === 'string' && selectedProject.calendarDays.length > 1) &&
                    <div className={`flex items-end gap-x-1 text-[#a59e9e] ${projectDaysRemaining === '?' ? 'hidden' : ''}`}>
                      <div className='opacity-80'>Calendar days remaining:</div><div className='text-white font-semibold'>{projectDaysRemaining}</div>
                    </div>
                  }
                  {/* Only show project completion date if we are able to calculate it from the project settings */}
                  {(DateStringMMDDYYYY.isValid(selectedProject.beginDate) && typeof selectedProject.calendarDays === 'string' && selectedProject.calendarDays.length > 1) &&
                    <div className={`flex items-end gap-x-1 text-[#a59e9e] ${projectCompletionDate === '?' ? 'hidden' : ''}`}>
                      <div className='opacity-80'>Completion date:</div><div className='text-white font-semibold'>{projectCompletionDate}</div>
                    </div>
                  }
                  {(memberCountInActiveProject <= 1 && SiteboardApiValidator[ADD_PROJECT_MEMBER].isUserIdAuthorized(_userId, selectedSubscription, selectedProject)) && 
                    <div className='flex gap-x-[9px] mt-2 items-center'>
                      <PushButton 
                        textColor='white'
                        bgColor='bg-stone-700'
                        hoverBgColor='bg-stone-600'
                        onClick={() => { 
                          siteboardAction(SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, { projectId: selectedProject.id });
                          setIsExpanded(false);
                        }}
                      >
                        <div className='flex flex-col justify-center items-center gap-1.5'>
                          <div className='flex justify-center items-center gap-1.5 font-bold'>
                            <TeamIcon size='1.4em' color='#f9a928' /> Invite Your Team
                          </div>
                          <div className='text-left font-normal'>Click to share access.</div>
                        </div>
                      </PushButton>
                    </div>
                  }
                </div>
                <PushButton 
                  textColor='white'
                  bgColor='bg-stone-700'
                  hoverBgColor='bg-stone-600'
                  onClick={() => { 
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id });
                    setIsExpanded(false);
                  }}
                >
                  <div className='flex items-center gap-x-[6px]'>
                    <SettingsIcon size='1.4em' /> 
                    <div>Project Settings</div>
                  </div>
                </PushButton>
              </div>
            }

            {/* Buttons */}
            <div className='flex flex-col mt-6 mx-1'>
              {selectedProject !== null &&
              <>
                {/* 3-week schedule button 
                <button  
                  className='flex items-center gap-x-[10px] group p-2'
                  onClick={() => { 
                    setPath('projectId#' + selectedProject.id + '#schedule'); 
                    setIsExpanded(false);
                  }}
                >
                  <div className={is3WeekSchedulePath ? 'hidden' : 'group-hover:hidden'}>
                    <GantChartIcon size='24' />
                  </div>
                  <div className={is3WeekSchedulePath ? '' : 'hidden group-hover:block'}>
                    <GantChartIcon size='24' color='#471cff' />
                  </div>
                  <p className={'' + (is3WeekSchedulePath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>3-Week Schedule</p>
                </button> */}

                {/* My projects button */}
                <button  
                  className='flex items-center gap-x-[10px] group p-2'
                  onClick={() => { 
                    setPath(''); 
                    setIsExpanded(false);
                  }}
                >
                  {/*<div className={isMyProjectsPath ? 'hidden' : 'group-hover:hidden'}>
                    <GlobeIcon size='24' />
                  </div>
                  <div className={isMyProjectsPath ? '' : 'hidden group-hover:block'}>
                    <GlobeIcon size='24' color='#fff168' />
                  </div>
                  <p className={'' + (isMyProjectsPath ? 'text-white' : 'text-white') + ' font-semibold'}>Projects</p>*/}
                  <div>
                    <GlobeIcon size='24' color='#fff168' />
                  </div>
                  <p className='text-white font-semibold'>All Projects</p>
                </button>

                {/* Project dashboard button */}
                {/*<button  
                  className='flex items-center gap-x-[10px] group'
                  onClick={() => { 
                    setPath('projectId#' + selectedProject.id); 
                    setIsExpanded(false);
                  }}
                >
                  <div className={isProjectDashbardPath ? 'hidden' : 'group-hover:hidden'}>
                    <DashboardIcon size='24' />
                  </div>
                  <div className={isProjectDashbardPath ? '' : 'hidden group-hover:block'}>
                    <DashboardIcon size='24' color='#1267e7' />
                  </div>
                  <p className={'' + (isProjectDashbardPath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>Project Dashboard</p>
                </button>*/}

                {/* Daily Reports button */}
                {process.env.REACT_APP_DAILY_REPORTS_ENABLE === 'true' &&
                  <button  
                    className='flex items-center gap-x-[10px] group p-2'
                    onClick={() => { 
                      setPath('projectId#' + selectedProject.id + '#dailyReports'); 
                      setIsExpanded(false);
                    }}
                  >
                    <div className={isDailyReportPath ? 'hidden' : 'group-hover:hidden'}>
                      <NotepadIcon size='24' color='#a59e9e' />
                    </div>
                    <div className={isDailyReportPath ? '' : 'hidden group-hover:block'}>
                      <NotepadIcon size='24' color='#55fa79' />
                    </div>
                    <p className={'' + (isDailyReportPath ? 'text-white' : 'text-[#a59e9e]') + ' font-semibold'}>Daily Reports</p>
                  </button>
                }
              </>
              }

              {/* Unread Message button */}
              <button 
                className='group flex justify-start items-center gap-x-2 p-2 font-semibold'
                onClick={() => {
                  setIsExpanded(false);
                  siteboardAction(SET_UNREAD_MESSAGES_DIALOGUE_OPEN, { isOpen: true });
                }}
              >
                {/*<div className='group-hover:hidden'><NotificationIcon color='#a59e9e' /></div> 
                <div className='hidden group-hover:block'><NotificationIcon color='#fff' /></div>*/}
                <div><NotificationIcon color='#fff' /></div> 
                <div className='flex justify-center items-center gap-x-1'>Notifications <AlertCountBubble totalUnreadMessages={totalUnreadMessages} /></div>
              </button>
            </div>

            {/* Trial remaining time and Upgrade To Standard Button */}
            {isTrialSubscription && SiteboardApiValidator[CREATE_STANDARD_SUBSCRIPTION_CHECKOUT].isUserIdAuthorized(_userId, ownedSubscription, null) &&
              <div className='mx-2 my-4'>
                <PushButton 
                  textColor='black'
                  bgColor='#f9a928'
                  hoverBgColor='#f79800'
                  onClick={() => { 
                    siteboardAction(SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, { isOpen: true }); 
                    setIsExpanded(false);
                  }}
                >
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-x-[6px]'>
                      <UpgradeIcon color='#000' /> 
                      <div>Upgrade to Standard Plan</div>
                    </div>
                    {trialTimeRemainingString !== null &&
                      <div className='text-red-700'>{trialTimeRemainingString}</div>
                    }
                  </div>
                </PushButton>
              </div>
            }
            {/* My Account info */}
            <div className='flex flex-col px-[5px] pb-[10px]'>
              <div className='w-full h-[1px] bg-[#888] my-[10px]'></div>
              <button
                className='flex items-center gap-x-2 text-left hover:bg-[#444] rounded-[8px] py-2 pl-1'
                onClick={() => { 
                  setProfileDialogueShowing(true); 
                  setIsExpanded(false);
                }}
                title='Profile'
              >
                <div>
                  <UserAvatar user={_users[_userId]} showOnlineStatus={false} />
                </div>
              </button>
            </div>
          </div>
        </>
      }
    </div>
  </>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _path: state.localPersistent.path,
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _lastViewedProjectId: state.localPersistent.lastViewedProjectId,
  _unreadScheduleMessageThreads: state.serverSynced.unreadScheduleMessageThreads,
  _subscriptions: state.serverSynced.subscriptions,
  _subscriptionInvites: state.serverSynced.subscriptionInvites,
}};
export default connect(mapStateToProps, {setPath, setProfileDialogueShowing, siteboardAction})(Topbar);
